import AddAPhotoOutlinedIcon from "@mui/icons-material/AddAPhotoOutlined";
import CancelIcon from "@mui/icons-material/Cancel";
import PrintIcon from "@mui/icons-material/Print";
import SearchIcon from "@mui/icons-material/Search";
import { AlertColor, Box, Button, Chip, Grid, IconButton, Paper, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, TextField, Tooltip, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { AxiosError, AxiosHeaders, AxiosResponse } from "axios";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { APIS, FEE_DUE_APIS, STUDENT_APIS } from "../common/api/apis";
import AddressFormParent from "../common/components/Address/Address";
import { Address } from "../common/shared/apis/AddressAPI";
import { Person } from "../common/shared/apis/PersonAPI";
import { getAxiosHeaders, patch, patchWOHeaders } from "../common/shared/AxiosAPI";
import { SharedAlert } from "../common/shared/components/SharedAlert";
import { SharedMUIList } from "../common/shared/components/SharedMUIList";
import { SharedDialog } from "../common/shared/SharedDialog";
import { EmergencyType, RELATIONS, Student, StudentDetailsView, StudentStatus, STUDENT_DETAILS_VIEW, STUDENT_STATUS } from "../common/shared/types";
import { getNationalities, getReligions, getStaticBloodGroups, getStaticGrades, getStaticLanguages, getStaticSections } from "../common/shared/Util";
import { StudentEditFeeDues } from "../components/feeDue/StudentEditFeeDues";
import { useStudent } from "../../features/student/hooks/useStudent";
import { printReceipt } from "../../features/fees/services/feePaymentService";
import { FeeDue } from "../../features/feeDue/types";
import { Person as DirectoryPerson } from "../../features/persons/types";
import PersonAssignSearchDialog, { NON_PARENT_RELATIONS } from "./PersonAssignSearchDialog";
import PersonEditDialog from "./PersonEditDialog";

const getStatusColor = (status: string | undefined): "success" | "error" | "warning" | "default" => {
  switch (status) {
    case STUDENT_STATUS.Active:
      return "success";
    case STUDENT_STATUS.Dropped:
      return "error";
    case STUDENT_STATUS.Transferred:
      return "warning";
    case STUDENT_STATUS.Registered:
      return "default";
    default:
      return "default";
  }
};

// Valid next statuses a student can move to from their current status.
const STATUS_TRANSITIONS: Record<string, string[]> = {
  [STUDENT_STATUS.Registered]: [STUDENT_STATUS.Active],
  [STUDENT_STATUS.Active]: [STUDENT_STATUS.Dropped, STUDENT_STATUS.Transferred],
  [STUDENT_STATUS.Dropped]: [STUDENT_STATUS.Active, STUDENT_STATUS.Transferred],
  [STUDENT_STATUS.Transferred]: [],
};

// Plain-language explanation of what each legal status transition does, shown for confirmation before saving.
const STATUS_TRANSITION_MESSAGES: Record<string, string> = {
  [`${STUDENT_STATUS.Registered}->${STUDENT_STATUS.Active}`]:
    "This will activate the student for the current academic year, using their current grade and section. A login account will be created for them if they don't already have one, and four fee due entries (one per term) will be generated. Once activated, grade and section cannot be changed — update them before activation if needed.",
  [`${STUDENT_STATUS.Dropped}->${STUDENT_STATUS.Active}`]:
    "This will reactivate the student. Any fee dues from before they were dropped remain on their account. Note: this only works if the student was dropped during the current academic year — if they were dropped in an earlier year, the server will reject this change.",
  [`${STUDENT_STATUS.Active}->${STUDENT_STATUS.Dropped}`]:
    "This will mark the student as dropped. Their enrollment record updates accordingly, and any existing fee dues stay on their account — they're not cancelled, in case the student returns.",
  [`${STUDENT_STATUS.Active}->${STUDENT_STATUS.Transferred}`]:
    "This will mark the student as transferred out. Their enrollment record updates accordingly, and every unpaid fee due — for this year and any prior year — will be cancelled, since the student is leaving for good.",
  [`${STUDENT_STATUS.Dropped}->${STUDENT_STATUS.Transferred}`]:
    "This will mark the student as transferred out. Their enrollment record updates accordingly, and every unpaid fee due — for this year and any prior year — will be cancelled, since the student is leaving for good.",
};

// Student.father/mother/guardian/emergency use the older Person shape (mobileNo: number).
// The Persons feature's Person uses mobileNo: string. Bridge the two at the boundary.
const toDirectoryPerson = (person: Person): DirectoryPerson => ({
  externalId: person?.externalId,
  name: person?.name,
  dob: person?.dob,
  qualification: person?.qualification,
  occupation: person?.occupation,
  monthlyIncome: person?.monthlyIncome,
  mobileNo: person?.mobileNo !== undefined && person?.mobileNo !== null ? String(person.mobileNo) : undefined,
  email: person?.email,
});

const FieldRow: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <Box display="flex" alignItems="center" gap={1.5}>
    <Typography
      variant="caption"
      sx={{ color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.4, minWidth: 96 }}
    >
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 600 }}>
      {value}
    </Typography>
  </Box>
);

export const StudentDetails: React.FC = () => {
  const location = useLocation();
  const admissionNo: string = location.state?.admissionNo;
  const [editStudent, setEditStudent] = useState<Student>(undefined);
  const [tmpStudent, setTmpStudent] = useState<Student>(undefined);//editStudent);
  //TODO refactor to remove globalStudent
  const [dbStudent, setDbStudent] = useState<Student>(undefined);//editStudent);
  const [postUpdate, setPostUpdate] = useState<number>(0);
  const [editingStudentDetails, setEditingStudentDetails] = useState<boolean>(false);

  const [fatherSearchOpen, setFatherSearchOpen] = useState<boolean>(false);
  const [fatherEditOpen, setFatherEditOpen] = useState<boolean>(false);
  const [motherSearchOpen, setMotherSearchOpen] = useState<boolean>(false);
  const [motherEditOpen, setMotherEditOpen] = useState<boolean>(false);

  const [guardianSearchOpen, setGuardianSearchOpen] = useState<boolean>(false);
  const [guardianEditOpen, setGuardianEditOpen] = useState<boolean>(false);

  const [emergencySearchOpen, setEmergencySearchOpen] = useState<boolean>(false);
  const [emergencyEditOpen, setEmergencyEditOpen] = useState<boolean>(false);

  //Shared dialog state
  const [sharedDialogState, setSharedDialogState] = useState(false);
  const [emergencyCIsGuardianDialog, setEmergencyCIsGuardianDialog] = useState(false);
  const [cancelFeeDueDialogState, setCancelFeeDueDialogState] = useState(false);
  const [statusChangeDialogState, setStatusChangeDialogState] = useState(false);

  const [editingAddress, setEditingAddress] = useState<boolean>(false);

  const { student, isLoading, actions } = useStudent(admissionNo);

  //useEffects

  useEffect(() => {
    console.log("Fetching student " + admissionNo)

    if (student && !isLoading) {
      setDbStudent(student);
      setEditStudent(student);
      setTmpStudent(student);
    }
  }, [student]);

  useEffect(() => {
    if (postUpdate === 0) {
      return;
    }
    postPatchRequest(undefined);
  }, [postUpdate]);
  //useEffects End

  const postPatchRequest = async (viewName: StudentDetailsView) => {
    let headers: AxiosHeaders = getAxiosHeaders();
    headers.set("oldAdmissionNo", dbStudent.admissionNo);
    headers.set("admissionNo", tmpStudent.admissionNo);
    headers.set("version", "2");

    let partialStudent: Partial<Student> = {};
    try {
      if (STUDENT_DETAILS_VIEW.Student === viewName) {
        partialStudent.name = tmpStudent.name;
        partialStudent.dob = tmpStudent.dob;
        partialStudent.religion = tmpStudent.religion;
        partialStudent.admissionNo = tmpStudent.admissionNo;
        partialStudent.externalId = tmpStudent.externalId;
        partialStudent.placeOfBirth = tmpStudent.placeOfBirth;
        partialStudent.nationality = tmpStudent.nationality;
        partialStudent.gradeName = tmpStudent.gradeName;
        partialStudent.gender = tmpStudent.gender;
        partialStudent.motherTongue = tmpStudent.motherTongue;
        partialStudent.bloodGroup = tmpStudent.bloodGroup;
        partialStudent.sectionName = tmpStudent.sectionName;
        partialStudent.active = tmpStudent.active;
        partialStudent.status = tmpStudent.status;

        await patch(APIS.STUDENTS, headers, partialStudent);
        setEditingStudentDetails(false)

        setDbStudent((prev) => ({
          ...prev, name: partialStudent.name, dob: partialStudent.dob, religion: partialStudent.religion,
          admissionNo: partialStudent.admissionNo, externalId: partialStudent.externalId, placeOfBirth: partialStudent.placeOfBirth,
          nationality: partialStudent.nationality, gradeName: partialStudent.gradeName, gender: partialStudent.gender, motherTongue: partialStudent.motherTongue,
          bloodGroup: partialStudent.bloodGroup, sectionName: partialStudent.sectionName, active: partialStudent.active, status: partialStudent.status
        }));

      } else {
        alert("Not implemented")
        return;
      }
      setSharedDialogState(true)
    } catch (err) {
      const error = err as AxiosError;
      setSharedAlert({
        open: true,
        severity: "error",
        message: (error.response?.data as string) || "Failed to update. Please try again.",
        title: "Update Failed",
      });
    }

  };

  const STUDENT_DETAILS_FIELDS: (keyof Student)[] = [
    "name", "dob", "religion", "admissionNo", "externalId", "placeOfBirth",
    "nationality", "gradeName", "gender", "motherTongue", "bloodGroup",
    "sectionName", "active", "status",
  ];

  // Sends only the Student Details fields that actually changed since the last save, instead of the whole section.
  const postChangedStudentDetails = async () => {
    const changedFields: Partial<Student> = {};
    STUDENT_DETAILS_FIELDS.forEach((field) => {
      if (tmpStudent[field] !== dbStudent[field]) {
        (changedFields as any)[field] = tmpStudent[field];
      }
    });

    if (Object.keys(changedFields).length === 0) {
      setEditingStudentDetails(false);
      return;
    }

    try {
      await patchWOHeaders(`${APIS.STUDENTS}?admissionNo=${encodeURIComponent(dbStudent.admissionNo)}`, changedFields);
      setEditingStudentDetails(false);
      setDbStudent((prev) => ({ ...prev, ...changedFields }));
      setSharedDialogState(true);
    } catch (err) {
      console.error(err);
      const error = err as AxiosError;
      setSharedAlert({
        open: true,
        severity: "error",
        message: (error.response?.data as string) || "Failed to update. Please try again.",
        title: "Update Failed",
      });
    }
  };

  // Entry point for the Student Details Save action. Intercepts with a confirmation
  // dialog when Status is changing, since a status change has real side effects
  // (enrollment records, fee dues, login accounts) beyond this one field.
  const handleSaveStudentDetails = () => {
    const fromStatus = dbStudent.status || STUDENT_STATUS.Active;
    const toStatus = tmpStudent.status || STUDENT_STATUS.Active;

    if (fromStatus === toStatus) {
      postChangedStudentDetails();
      return;
    }

    if (fromStatus === STUDENT_STATUS.Registered && toStatus === STUDENT_STATUS.Active && tmpStudent.sectionName === "DNE") {
      setSharedAlert({
        open: true,
        severity: "error",
        message: "Assign a valid section to this student before activating them.",
        title: "Cannot Activate",
      });
      return;
    }

    setStatusChangeDialogState(true);
  };

  // Assign an existing Person (found via search in PersonAssignSearchDialog) as this student's
  // Father/Mother/Guardian/Emergency contact — never creates or edits a person from here; editing a
  // person's own fields happens exclusively in the Persons Directory.
  const saveFatherAssignment = async (person: DirectoryPerson) => {
    try {
      await patchWOHeaders(`${APIS.STUDENTS}?admissionNo=${encodeURIComponent(dbStudent.admissionNo)}`, {
        father: { externalId: person.externalId },
      });
      actions.reload();
      setSharedDialogState(true);
    } catch (err) {
      console.error(err);
      const error = err as AxiosError;
      setSharedAlert({
        open: true,
        severity: "error",
        message: (error.response?.data as string) || "Failed to save father.",
        title: "Save Failed",
      });
      throw err;
    }
  };

  const saveMotherAssignment = async (person: DirectoryPerson) => {
    try {
      await patchWOHeaders(`${APIS.STUDENTS}?admissionNo=${encodeURIComponent(dbStudent.admissionNo)}`, {
        mother: { externalId: person.externalId },
      });
      actions.reload();
      setSharedDialogState(true);
    } catch (err) {
      console.error(err);
      const error = err as AxiosError;
      setSharedAlert({
        open: true,
        severity: "error",
        message: (error.response?.data as string) || "Failed to save mother.",
        title: "Save Failed",
      });
      throw err;
    }
  };

  const saveGuardianAssignment = async (person: DirectoryPerson, relation: string) => {
    try {
      await patchWOHeaders(`${APIS.STUDENTS}?admissionNo=${encodeURIComponent(dbStudent.admissionNo)}`, {
        guardian: { externalId: person.externalId },
        guardianContactRelation: relation,
      });
      actions.reload();
      setSharedDialogState(true);
    } catch (err) {
      console.error(err);
      const error = err as AxiosError;
      setSharedAlert({
        open: true,
        severity: "error",
        message: (error.response?.data as string) || "Failed to save guardian.",
        title: "Save Failed",
      });
      throw err;
    }
  };

  const saveEmergencyAssignment = async (person: DirectoryPerson, relation: string) => {
    try {
      await patchWOHeaders(`${APIS.STUDENTS}?admissionNo=${encodeURIComponent(dbStudent.admissionNo)}`, {
        emergency: { externalId: person.externalId },
        emergencyContactRelation: relation,
      });
      actions.reload();
      setSharedDialogState(true);
    } catch (err) {
      console.error(err);
      const error = err as AxiosError;
      setSharedAlert({
        open: true,
        severity: "error",
        message: (error.response?.data as string) || "Failed to save emergency contact.",
        title: "Save Failed",
      });
      throw err;
    }
  };

  // Relation-only change (from the Edit dialog) — the person isn't changing, so no externalId is sent.
  const saveGuardianRelation = async (relation: string) => {
    try {
      await patchWOHeaders(`${APIS.STUDENTS}?admissionNo=${encodeURIComponent(dbStudent.admissionNo)}`, {
        guardianContactRelation: relation,
      });
      actions.reload();
      setSharedDialogState(true);
    } catch (err) {
      console.error(err);
      const error = err as AxiosError;
      setSharedAlert({
        open: true,
        severity: "error",
        message: (error.response?.data as string) || "Failed to update guardian relation.",
        title: "Save Failed",
      });
      throw err;
    }
  };

  const saveEmergencyRelation = async (relation: string) => {
    try {
      await patchWOHeaders(`${APIS.STUDENTS}?admissionNo=${encodeURIComponent(dbStudent.admissionNo)}`, {
        emergencyContactRelation: relation,
      });
      actions.reload();
      setSharedDialogState(true);
    } catch (err) {
      console.error(err);
      const error = err as AxiosError;
      setSharedAlert({
        open: true,
        severity: "error",
        message: (error.response?.data as string) || "Failed to update emergency relation.",
        title: "Save Failed",
      });
      throw err;
    }
  };

  const [sharedAlert, setSharedAlert] = useState({
    open: false,
    severity: "info" as AlertColor,
    message: "",
    title: "",
  });

  const [tab, setTab] = React.useState<StudentDetailsView>(STUDENT_DETAILS_VIEW.Father);

  const saveStudentDetail = (e: React.FormEvent) => {
    e.preventDefault();

    setPostUpdate(postUpdate + 1);
  };

  useEffect(() => {
    if (dbStudent) {
      const dbStudentJson = JSON.parse(JSON.stringify(dbStudent));
      setTmpStudent(dbStudentJson);
    }
  }, [dbStudent]);

  /**
   * Generic function to update any fields of student and sync global state
   * @param updatedFields Partial<Student> - fields to update
   */
  const patchStudentAndSyncGlobalState = async (updatedFields: Partial<Student>) => {
    // LEARNING
    // Merge current student with only the updated fields
    //Understand shallow merge (...) and deep merge.
    //When you use the below approach entire child object will replaced. It don't retain missing values from the parent
    const updatedStudent: Student = { ...dbStudent, ...updatedFields, };

    try {
      let headers: AxiosHeaders = getAxiosHeaders();
      headers.set("oldAdmissionNo", dbStudent.admissionNo);
      headers.set("admissionNo", tmpStudent.admissionNo);
      const response = await patch(STUDENT_APIS.STUDENTS, headers, updatedStudent); // PATCH to update
      setDbStudent(response.data); // sync global state
      setTmpStudent(response.data);
      setSharedDialogState(true);
    } catch (err) {
      console.error(err);
      alert("Failed to update student");
    }
  };

  const handleTabChange = (e: React.SyntheticEvent, tab: StudentDetailsView) => {
    setTab(tab);
  };

  /**
* Generic function to update any fields of student and sync global state
* @param updatedFields Partial<Student> - fields to update
*/
  const patchStudentAddress = async (partialStudent: Partial<Student>) => {

    try {
      let headers: AxiosHeaders = getAxiosHeaders();
      headers.set("oldAdmissionNo", dbStudent.admissionNo);
      headers.set("admissionNo", dbStudent.admissionNo);
      headers.set("version", "2");

      await patch(APIS.STUDENTS, headers, partialStudent);
      setEditingAddress(false)
      const presentAddress = partialStudent.present;
      const permanentAddress = partialStudent.permanent;
      const permanentAddIsSameAsPresent = JSON.stringify(presentAddress) === JSON.stringify(permanentAddress);
      setDbStudent((prev) => ({ ...prev, present: presentAddress, permanent: permanentAddress, permanentAddIsSameAsPresent: permanentAddIsSameAsPresent }));
      setSharedDialogState(true);
    } catch (err) {
      console.error(err);
      alert("Failed to update address");
    }

  };

  const cancelFeeDue = async () => {
    try {
      const headers = getAxiosHeaders();
      headers.set("admissionNo", student.admissionNo);
      const response: AxiosResponse = await patch(FEE_DUE_APIS.CANCEL, headers, null);
      if (response.status === 200) {
        actions.reload();
        setSharedAlert({
          open: true,
          severity: "success",
          message: "Fee due cancelled successfully.",
          title: "",
        });
      }
    } catch (err) {
      console.error(err);
      setSharedAlert({
        open: true,
        severity: "error",
        message: "Failed to cancel fee due.",
        title: "",
      });
    }
  }
  if (!editStudent) return <></>

  return (
    <Box>
      <>
        <SharedAlert
          open={sharedAlert.open}
          onClose={() => setSharedAlert((prev) => ({ ...prev, open: false }))}
          severity={sharedAlert.severity}
          message={sharedAlert.message}
          title={sharedAlert.title}
        />
        <SharedDialog
          message={"Update was successful!"}
          dialogState={sharedDialogState}
          setDialogState={(value: boolean) => setSharedDialogState(value)}
          proceedButtonRequired={false}
          cancelOptionText="Ok"
        />
        <SharedDialog
          message={"You entered guardian details under emergency contact information, so the emergency contact type has been set to Guardian."}
          dialogState={emergencyCIsGuardianDialog}
          setDialogState={(value: boolean) => setEmergencyCIsGuardianDialog(value)}
          proceedButtonRequired={false}
          cancelOptionText="Ok"
        />
        <SharedDialog
          heading="Cancel Fee Due"
          message={"This action cannot be rolled back. Are you sure you want to proceed?"}
          dialogState={cancelFeeDueDialogState}
          setDialogState={(value: boolean) => setCancelFeeDueDialogState(value)}
          proceedButtonRequired={true}
          onProceed={() => {
            setCancelFeeDueDialogState(false);
            cancelFeeDue();
          }}
        />
        <SharedDialog
          heading={`Confirm status change: ${dbStudent?.status || STUDENT_STATUS.Active} → ${tmpStudent?.status || STUDENT_STATUS.Active}`}
          message={
            STATUS_TRANSITION_MESSAGES[`${dbStudent?.status || STUDENT_STATUS.Active}->${tmpStudent?.status || STUDENT_STATUS.Active}`] ||
            "This will change the student's status."
          }
          dialogState={statusChangeDialogState}
          setDialogState={(value: boolean) => setStatusChangeDialogState(value)}
          proceedButtonRequired={true}
          onProceed={() => {
            setStatusChangeDialogState(false);
            postChangedStudentDetails();
          }}
        />
      </>
      <Paper elevation={3} sx={{ p: 2, borderRadius: 3 }}>
        <Grid container spacing={3}>
          {/* Student Photo */}
          <Grid>
            <Box
              sx={{
                width: 180,
                height: 180,
                border: "2px dashed",
                borderColor: "grey.300",
                bgcolor: "grey.50",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                borderRadius: 3,
                boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.02)",
              }}
            >
              <AddAPhotoOutlinedIcon sx={{ fontSize: 34, color: "grey.400" }} />
              <Typography variant="caption" sx={{ color: "text.disabled" }}>
                No photo added
              </Typography>
            </Box>
          </Grid>
          {/* Student details here */}
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Grid>
              <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1.5, mt: 2, ml: 2 }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                    Student Details
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.disabled" }}>
                    {editingStudentDetails ? "Editing — remember to save" : "Tap Edit to update"}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 2 }}>
                  {editingStudentDetails ? (
                    <>
                      <Typography
                        component="span"
                        variant="body2"
                        onClick={(e) => {
                          handleSaveStudentDetails();
                        }}
                        sx={{
                          fontWeight: 500,
                          color: "success.main",
                          cursor: "pointer",
                          borderBottom: "1px solid transparent",
                          "&:hover": { borderColor: "currentcolor" },
                        }}
                      >
                        Save
                      </Typography>
                      <Typography
                        component="span"
                        variant="body2"
                        onClick={(e) => {
                          const globalStudentJson = JSON.parse(JSON.stringify(dbStudent));
                          setTmpStudent(globalStudentJson);
                          setEditingStudentDetails(false);
                        }}
                        sx={{
                          fontWeight: 500,
                          color: "text.secondary",
                          cursor: "pointer",
                          borderBottom: "1px solid transparent",
                          "&:hover": { borderColor: "currentcolor" },
                        }}
                      >
                        Discard
                      </Typography>
                    </>
                  ) : (
                    <Typography
                      component="span"
                      variant="body2"
                      onClick={(e) => setEditingStudentDetails(true)}
                      sx={{
                        fontWeight: 500,
                        color: "primary.main",
                        cursor: "pointer",
                        borderBottom: "1px solid transparent",
                        "&:hover": { borderColor: "currentcolor" },
                      }}
                    >
                      Edit
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>
            <Grid>
              <Box sx={{ display: "flex", flexDirection: "row", gap: 4 }} component={"form"} onSubmit={(e: React.FormEvent<HTMLFormElement>) => saveStudentDetail(e)}>
                {/* Identity */}
                <Grid sx={{ mt: 2, ml: 3 }}>
                  {editingStudentDetails ? (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <TextField
                        label="Name"
                        value={tmpStudent.name}
                        onChange={(e) => setTmpStudent({ ...tmpStudent, name: e.target.value })}
                        size="small"
                        sx={{ width: "160px" }}
                      />
                      <SharedMUIList
                        lable="Gender"
                        value={tmpStudent.gender}
                        list={["M", "F"]}
                        onChange={(value) => setTmpStudent({ ...tmpStudent, gender: value })}
                        width="160px"
                      />
                      <DatePicker
                        label="Date Of Birth"
                        format="DD-MM-YYYY" // 👈 supports custom format
                        value={tmpStudent.dob ? dayjs(tmpStudent.dob, "DD-MM-YYYY") : null}
                        onChange={(newValue) => setTmpStudent({ ...tmpStudent, dob: newValue ? newValue.format("DD-MM-YYYY") : "" })}
                        slotProps={{
                          textField: { size: "small", sx: { width: "160px" } }, // 👈 makes it small like TextField
                        }}
                      />
                      <TextField
                        label="City Of Birth"
                        value={tmpStudent.placeOfBirth}
                        onChange={(e) => setTmpStudent({ ...tmpStudent, placeOfBirth: e.target.value })}
                        size="small"
                        required
                        sx={{ width: "160px" }}
                      />
                    </Box>
                  ) : (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <FieldRow label="Name" value={tmpStudent.name} />
                      <FieldRow label="Gender" value={tmpStudent.gender} />
                      <FieldRow label="Date Of Birth" value={tmpStudent.dob} />
                      <FieldRow label="City Of Birth" value={tmpStudent.placeOfBirth} />
                    </Box>
                  )}
                </Grid>

                {/* Background */}
                <Grid sx={{ mt: 2, ml: 3 }}>
                  {editingStudentDetails ? (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <SharedMUIList
                        lable="Religion"
                        value={tmpStudent.religion || " "}
                        list={getReligions()}
                        onChange={(value) => setTmpStudent({ ...tmpStudent, religion: value })}
                        width="160px"
                      />
                      <SharedMUIList
                        lable="Nationality"
                        value={tmpStudent.nationality || " "}
                        list={getNationalities()}
                        onChange={(value) => setTmpStudent({ ...tmpStudent, nationality: value })}
                        width="160px"
                      />
                      <SharedMUIList
                        lable="Mother Tongue"
                        value={tmpStudent.motherTongue || " "}
                        list={getStaticLanguages()}
                        onChange={(value) => setTmpStudent({ ...tmpStudent, motherTongue: value })}
                        width="160px"
                      />
                      <SharedMUIList
                        lable="Blood Group"
                        value={tmpStudent.bloodGroup || " "}
                        list={getStaticBloodGroups()}
                        onChange={(value) => setTmpStudent({ ...tmpStudent, bloodGroup: value })}
                        width="160px"
                      />
                    </Box>
                  ) : (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <FieldRow label="Religion" value={tmpStudent.religion} />
                      <FieldRow label="Nationality" value={tmpStudent.nationality} />
                      <FieldRow label="Mother Tongue" value={tmpStudent.motherTongue} />
                      <FieldRow label="Blood Group" value={tmpStudent.bloodGroup} />
                    </Box>
                  )}
                </Grid>

                {/* Enrollment */}
                <Grid sx={{ mt: 2, ml: 3 }}>
                  {editingStudentDetails ? (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <TextField
                        label="Admission No"
                        value={tmpStudent.admissionNo}
                        onChange={(e) => setTmpStudent({ ...tmpStudent, admissionNo: e.target.value })}
                        size="small"
                        sx={{ width: "160px" }}
                      />
                      {/* Grade/Section only change while a student is REGISTERED (not yet placed); once
                          ACTIVE/DROPPED/TRANSFERRED they're moved via a dedicated promotion/transfer flow. */}
                      {(tmpStudent.status || STUDENT_STATUS.Active) === STUDENT_STATUS.Registered ? (
                        <SharedMUIList
                          lable="Grade"
                          value={tmpStudent.gradeName || " "}
                          list={getStaticGrades()}
                          onChange={(value) => setTmpStudent({ ...tmpStudent, gradeName: value })}
                          width="160px"
                        />
                      ) : (
                        <FieldRow label="Grade" value={tmpStudent.gradeName} />
                      )}
                      {(tmpStudent.status || STUDENT_STATUS.Active) === STUDENT_STATUS.Registered ? (
                        <SharedMUIList
                          lable="Section"
                          value={tmpStudent.sectionName || " "}
                          list={getStaticSections()}
                          onChange={(value) => setTmpStudent({ ...tmpStudent, sectionName: value })}
                          width="160px"
                        />
                      ) : (
                        <FieldRow label="Section" value={tmpStudent.sectionName} />
                      )}
                      <SharedMUIList
                        lable="Status"
                        value={tmpStudent.status || STUDENT_STATUS.Active}
                        list={[
                          dbStudent.status || STUDENT_STATUS.Active,
                          ...(STATUS_TRANSITIONS[dbStudent.status || STUDENT_STATUS.Active] || []),
                        ]}
                        onChange={(value) => setTmpStudent({ ...tmpStudent, status: value as StudentStatus })}
                        width="160px"
                      />
                    </Box>
                  ) : (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <FieldRow label="Admission No" value={tmpStudent.admissionNo} />
                      <FieldRow label="Grade" value={tmpStudent.gradeName} />
                      <FieldRow label="Section" value={tmpStudent.sectionName} />
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.4, minWidth: 96 }}
                        >
                          Status
                        </Typography>
                        <Chip
                          label={tmpStudent.status || STUDENT_STATUS.Active}
                          size="small"
                          color={getStatusColor(tmpStudent.status)}
                          sx={{ fontWeight: 600 }}
                        />
                      </Box>
                    </Box>
                  )}
                </Grid>
              </Box>
            </Grid>
          </Box>
          {/* Student detail here end */}
        </Grid>

        {/* Tabs Section */}
        <Box mt={3}>
          <Tabs value={tab} onChange={(e, newValue) => handleTabChange(e, newValue)} textColor="primary" indicatorColor="primary">
            <Tab label="Father" value={STUDENT_DETAILS_VIEW.Father} />
            <Tab label="Mother" value={STUDENT_DETAILS_VIEW.Mother} />
            <Tab label="Guardian" value={STUDENT_DETAILS_VIEW.Guardian} />
            <Tab label="Emergency" value={STUDENT_DETAILS_VIEW.Emergency} />
            <Tab label="Previous Schools" />
            <Tab label="Notes" />
            <Tab label="Address" value={STUDENT_DETAILS_VIEW.Address} />
            <Tab label="Fee Dues" value={STUDENT_DETAILS_VIEW.FeeDues} />
            <Tab label="Fee Receipts" value={STUDENT_DETAILS_VIEW.Fees} />
            <Tab label="Siblings" />
            <Tab label="Promote" />
            <Tab label="Delete" />
          </Tabs>

          <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 1 }}>
            {tab === STUDENT_DETAILS_VIEW.Father && (
              <Box sx={{ ml: 2, mt: 2, display: "flex", flexDirection: "column", gap: 2, maxWidth: "100%" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Currently Assigned
                </Typography>

                {dbStudent.father?.externalId ? (
                  <Box
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                      p: 2,
                      display: "grid",
                      gridTemplateColumns: "repeat(4, 1fr)",
                      gap: 1.5,
                    }}
                  >
                    <FieldRow label="External ID" value={dbStudent.father.externalId} />
                    <FieldRow label="Name" value={dbStudent.father.name} />
                    <FieldRow label="Mobile No" value={dbStudent.father.mobileNo} />
                    <FieldRow label="Email" value={dbStudent.father.email} />
                    <FieldRow label="Date Of Birth" value={dbStudent.father.dob} />
                    <FieldRow label="Qualification" value={dbStudent.father.qualification} />
                    <FieldRow label="Occupation" value={dbStudent.father.occupation} />
                    <FieldRow label="Monthly Income" value={dbStudent.father.monthlyIncome} />
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No father assigned yet.
                  </Typography>
                )}

                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button variant="outlined" size="small" startIcon={<SearchIcon />} onClick={() => setFatherSearchOpen(true)} sx={{ width: "fit-content" }}>
                    Search
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setFatherEditOpen(true)}
                    disabled={!dbStudent.father?.externalId}
                    sx={{ width: "fit-content" }}
                  >
                    Edit
                  </Button>
                </Box>

                <PersonAssignSearchDialog
                  open={fatherSearchOpen}
                  title="Search Father"
                  onClose={() => setFatherSearchOpen(false)}
                  onSave={saveFatherAssignment}
                  fixedRelation={RELATIONS.Father}
                />
                <PersonEditDialog
                  open={fatherEditOpen}
                  title="Edit Father"
                  person={toDirectoryPerson(dbStudent.father)}
                  onClose={() => setFatherEditOpen(false)}
                  onSaved={actions.reload}
                  mobileRequired={
                    !!dbStudent.father?.externalId &&
                    (dbStudent.guardian?.externalId === dbStudent.father.externalId ||
                      dbStudent.emergency?.externalId === dbStudent.father.externalId)
                  }
                  mobileRequiredMessage="Father mobile number is mandatory while he is the assigned guardian or emergency contact. Change the guardian/emergency contact first to remove the mobile number."
                />
              </Box>
            )}
            {tab === STUDENT_DETAILS_VIEW.Mother && (
              <Box sx={{ ml: 2, mt: 2, display: "flex", flexDirection: "column", gap: 2, maxWidth: "100%" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Currently Assigned
                </Typography>

                {dbStudent.mother?.externalId ? (
                  <Box
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                      p: 2,
                      display: "grid",
                      gridTemplateColumns: "repeat(4, 1fr)",
                      gap: 1.5,
                    }}
                  >
                    <FieldRow label="External ID" value={dbStudent.mother.externalId} />
                    <FieldRow label="Name" value={dbStudent.mother.name} />
                    <FieldRow label="Mobile No" value={dbStudent.mother.mobileNo} />
                    <FieldRow label="Email" value={dbStudent.mother.email} />
                    <FieldRow label="Date Of Birth" value={dbStudent.mother.dob} />
                    <FieldRow label="Qualification" value={dbStudent.mother.qualification} />
                    <FieldRow label="Occupation" value={dbStudent.mother.occupation} />
                    <FieldRow label="Monthly Income" value={dbStudent.mother.monthlyIncome} />
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No mother assigned yet.
                  </Typography>
                )}

                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button variant="outlined" size="small" startIcon={<SearchIcon />} onClick={() => setMotherSearchOpen(true)} sx={{ width: "fit-content" }}>
                    Search
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setMotherEditOpen(true)}
                    disabled={!dbStudent.mother?.externalId}
                    sx={{ width: "fit-content" }}
                  >
                    Edit
                  </Button>
                </Box>

                <PersonAssignSearchDialog
                  open={motherSearchOpen}
                  title="Search Mother"
                  onClose={() => setMotherSearchOpen(false)}
                  onSave={saveMotherAssignment}
                  fixedRelation={RELATIONS.Mother}
                />
                <PersonEditDialog
                  open={motherEditOpen}
                  title="Edit Mother"
                  person={toDirectoryPerson(dbStudent.mother)}
                  onClose={() => setMotherEditOpen(false)}
                  onSaved={actions.reload}
                  mobileRequired={
                    !!dbStudent.mother?.externalId &&
                    (dbStudent.guardian?.externalId === dbStudent.mother.externalId ||
                      dbStudent.emergency?.externalId === dbStudent.mother.externalId)
                  }
                  mobileRequiredMessage="Mother mobile number is mandatory while she is the assigned guardian or emergency contact. Change the guardian/emergency contact first to remove the mobile number."
                />
              </Box>
            )}

            {/* Guardian Tab */}
            {tab === STUDENT_DETAILS_VIEW.Guardian && (
              <Box sx={{ ml: 2, mt: 2, display: "flex", flexDirection: "column", gap: 2, maxWidth: "100%" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Currently Assigned
                </Typography>

                {dbStudent.guardian?.externalId ? (
                  <Box
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                      p: 2,
                      display: "grid",
                      gridTemplateColumns: "repeat(4, 1fr)",
                      gap: 1.5,
                    }}
                  >
                    <FieldRow label="Relation" value={dbStudent.guardianContactRelation} />
                    <FieldRow label="External ID" value={dbStudent.guardian.externalId} />
                    <FieldRow label="Name" value={dbStudent.guardian.name} />
                    <FieldRow label="Mobile No" value={dbStudent.guardian.mobileNo} />
                    <FieldRow label="Email" value={dbStudent.guardian.email} />
                    <FieldRow label="Date Of Birth" value={dbStudent.guardian.dob} />
                    <FieldRow label="Qualification" value={dbStudent.guardian.qualification} />
                    <FieldRow label="Occupation" value={dbStudent.guardian.occupation} />
                    <FieldRow label="Monthly Income" value={dbStudent.guardian.monthlyIncome} />
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No guardian assigned yet.
                  </Typography>
                )}

                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button variant="outlined" size="small" startIcon={<SearchIcon />} onClick={() => setGuardianSearchOpen(true)} sx={{ width: "fit-content" }}>
                    Search
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setGuardianEditOpen(true)}
                    disabled={!dbStudent.guardian?.externalId}
                    sx={{ width: "fit-content" }}
                  >
                    Edit
                  </Button>
                </Box>

                <PersonAssignSearchDialog
                  open={guardianSearchOpen}
                  title="Search Guardian"
                  onClose={() => setGuardianSearchOpen(false)}
                  onSave={saveGuardianAssignment}
                />
                <PersonEditDialog
                  open={guardianEditOpen}
                  title="Edit Guardian"
                  person={toDirectoryPerson(dbStudent.guardian)}
                  onClose={() => setGuardianEditOpen(false)}
                  onSaved={actions.reload}
                  mobileRequired
                  mobileRequiredMessage="Mobile number is required for a guardian contact."
                  relation={
                    dbStudent.guardianContactRelation !== RELATIONS.Father && dbStudent.guardianContactRelation !== RELATIONS.Mother
                      ? {
                          value: dbStudent.guardianContactRelation,
                          options: NON_PARENT_RELATIONS,
                          onChange: saveGuardianRelation,
                        }
                      : undefined
                  }
                />
              </Box>
            )}
            {tab === STUDENT_DETAILS_VIEW.Emergency && (
              <Box sx={{ ml: 2, mt: 2, display: "flex", flexDirection: "column", gap: 2, maxWidth: "100%" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Currently Assigned
                </Typography>

                {dbStudent.emergency?.externalId ? (
                  <Box
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                      p: 2,
                      display: "grid",
                      gridTemplateColumns: "repeat(4, 1fr)",
                      gap: 1.5,
                    }}
                  >
                    <FieldRow label="Relation" value={dbStudent.emergencyContactRelation} />
                    <FieldRow label="External ID" value={dbStudent.emergency.externalId} />
                    <FieldRow label="Name" value={dbStudent.emergency.name} />
                    <FieldRow label="Mobile No" value={dbStudent.emergency.mobileNo} />
                    <FieldRow label="Email" value={dbStudent.emergency.email} />
                    <FieldRow label="Date Of Birth" value={dbStudent.emergency.dob} />
                    <FieldRow label="Qualification" value={dbStudent.emergency.qualification} />
                    <FieldRow label="Occupation" value={dbStudent.emergency.occupation} />
                    <FieldRow label="Monthly Income" value={dbStudent.emergency.monthlyIncome} />
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No emergency contact assigned yet.
                  </Typography>
                )}

                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button variant="outlined" size="small" startIcon={<SearchIcon />} onClick={() => setEmergencySearchOpen(true)} sx={{ width: "fit-content" }}>
                    Search
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setEmergencyEditOpen(true)}
                    disabled={!dbStudent.emergency?.externalId}
                    sx={{ width: "fit-content" }}
                  >
                    Edit
                  </Button>
                </Box>

                <PersonAssignSearchDialog
                  open={emergencySearchOpen}
                  title="Search Emergency Contact"
                  onClose={() => setEmergencySearchOpen(false)}
                  onSave={saveEmergencyAssignment}
                  relationOptions={[...NON_PARENT_RELATIONS, EmergencyType.GUARDIAN]}
                />
                <PersonEditDialog
                  open={emergencyEditOpen}
                  title="Edit Emergency Contact"
                  person={toDirectoryPerson(dbStudent.emergency)}
                  onClose={() => setEmergencyEditOpen(false)}
                  onSaved={actions.reload}
                  mobileRequired
                  mobileRequiredMessage="Mobile number is required for an emergency contact."
                  relation={
                    dbStudent.emergencyContactRelation !== RELATIONS.Father && dbStudent.emergencyContactRelation !== RELATIONS.Mother
                      ? {
                          value: dbStudent.emergencyContactRelation,
                          options: [...NON_PARENT_RELATIONS, EmergencyType.GUARDIAN],
                          onChange: saveEmergencyRelation,
                        }
                      : undefined
                  }
                />
              </Box>
            )}
            {tab === STUDENT_DETAILS_VIEW.Address && (
              <>
                <AddressFormParent
                  editing={editingAddress}
                  setEditing={setEditingAddress}
                  presentAddress={dbStudent.present}
                  permanentAddress={dbStudent.permanent}
                  onSubmit={(present: Address, permanent: Address) => {
                    patchStudentAddress({ present: present, permanent: permanent });
                  }}
                />
              </>
            )}
            {tab === STUDENT_DETAILS_VIEW.FeeDues && (
              <>
                {/* LEARNING - "Access .length only if student.feeDues is not null or undefined." */}
                {student.feeDues?.length > 0 ? (
                  <TableContainer className="mui-table-container-style" sx={{ marginLeft: "50px", width: "700px" }}>
                    <Table size="small" className="border">
                      <TableHead>
                        <TableRow className="bg-gray-100">
                          <TableCell className="font-semibold">Academic Year</TableCell>
                          <TableCell className="font-semibold">Grade</TableCell>
                          <TableCell className="font-semibold">Section</TableCell>
                          <TableCell className="font-semibold">Term</TableCell>
                          <TableCell className="font-semibold">Fee</TableCell>
                          <TableCell className="font-semibold">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {student.feeDues.map((row: FeeDue, index: number) => (
                          <TableRow key={index}>
                            <TableCell>{row.academicYear}</TableCell>
                            <TableCell>{row.gradeName}</TableCell>
                            <TableCell>{row.sectionName}</TableCell>
                            <TableCell>{row.terms}</TableCell>
                            <TableCell>₹ {row.payable}</TableCell>
                            <TableCell>
                              <Box>
                                <Grid>
                                  <Tooltip title="Cancel">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => setCancelFeeDueDialogState(true)}
                                      disabled={index !== 0}
                                    >
                                      <CancelIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Grid>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography>No data available</Typography>
                )}
              </>
            )}
            {tab === STUDENT_DETAILS_VIEW.Fees && (
              <TableContainer component={Paper} variant="outlined"
                sx={{ ml: 2, width: "fit-content", maxHeight: 250, overflowY: "auto", }}>
                <Table stickyHeader size="small" aria-label="Fee receipts table">
                  <TableHead>
                    <TableRow>
                      <TableCell align="center">Receipt No</TableCell>
                      <TableCell align="center">Date</TableCell>
                      <TableCell align="center">Grade-Sec</TableCell>
                      <TableCell align="center">Terms</TableCell>
                      <TableCell align="center">Amount</TableCell>
                      <TableCell align="center">Discount</TableCell>
                      <TableCell align="center">Payment Mode</TableCell>
                      <TableCell align="center">Collected By</TableCell>
                      <TableCell align="center">Academic Year</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!dbStudent.feeResponseDtos || dbStudent.feeResponseDtos.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} align="center">
                          <Typography variant="body2" color="text.secondary">
                            No fee receipts found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      dbStudent.feeResponseDtos.map((receipt, index) => (
                        <TableRow key={index} hover>
                          <TableCell align="center">{receipt.receiptNo}</TableCell>
                          <TableCell align="center">{receipt.receiptDate}</TableCell>
                          <TableCell align="center">{`${receipt.grade} - ${receipt.section}`}</TableCell>
                          <TableCell align="center">{receipt.terms}</TableCell>
                          <TableCell align="center">{receipt.amount}</TableCell>
                          <TableCell align="center">{receipt.discount}</TableCell>
                          <TableCell align="center">{receipt.paymentMode}</TableCell>
                          <TableCell align="center">{receipt.collectedBy}</TableCell>
                          <TableCell align="center">{receipt.academicYear}</TableCell>
                          <TableCell align="center">
                            <Tooltip title="Print">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => printReceipt(Number(receipt.receiptNo))}
                              >
                                <PrintIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};
